// src/components/Artists.js
import { useState, useEffect } from 'react';
import SpotifyWebApi from 'spotify-web-api-js';
import Navbar from './Navbar';

const spotifyApi = new SpotifyWebApi();

const Artists = ({ token }) => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    const fetchArtists = async () => {
      if (!token) {
        setError('No token available');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        spotifyApi.setAccessToken(token);

        // First, let's check if the token is valid
        const me = await spotifyApi.getMe();
        setDebugInfo(prev => ({ ...prev, user: me }));

        // Now fetch followed artists
        const response = await spotifyApi.getFollowedArtists({ limit: 50 });
        setDebugInfo(prev => ({ ...prev, artistsResponse: response }));

        if (response && response.artists && response.artists.items) {
          setArtists(response.artists.items);
        } else {
          setError('Invalid response format from Spotify API');
        }
      } catch (err) {
        console.error('Full error:', err);
        setError(err.message || 'Failed to fetch artists');
        setDebugInfo(prev => ({ ...prev, error: err }));
      } finally {
        setLoading(false);
      }
    };

    fetchArtists();
  }, [token]);

  const retryFetch = () => {
    setLoading(true);
    setError(null);
    spotifyApi.setAccessToken(token);
    // Force token refresh by logging out
    window.localStorage.clear();
    window.location.href = '/';
  };

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-9 p-4">
            <h2>Loading artists...</h2>
            <div className="spinner-border text-success" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
        <div>
        <Navbar />
  
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-9 p-4">
            <div className="alert alert-danger">
              <h4 className="alert-heading">Error Loading Artists</h4>
              <p>{error}</p>
              <hr />
              <p className="mb-0">
                <button 
                  className="btn btn-outline-danger me-2"
                  onClick={retryFetch}
                >
                  Try Again
                </button>
                <button 
                  className="btn btn-outline-primary"
                  onClick={() => setDebugInfo(prev => ({ ...prev, show: !prev?.show }))}
                >
                  {debugInfo?.show ? 'Hide' : 'Show'} Debug Info
                </button>
              </p>
              {debugInfo?.show && (
                <div className="mt-3">
                  <pre className="bg-light p-3 rounded">
                    {JSON.stringify(debugInfo, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
    );
  }

  return (
    <div>
    <Navbar />

    <div className="container-fluid">
      <div className="row">
        <div className="col-md-9 p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>Your Followed Artists</h2>
            <button 
              className="btn btn-outline-secondary"
              onClick={() => setDebugInfo(prev => ({ ...prev, show: !prev?.show }))}
            >
              Debug Info
            </button>
          </div>
          
          {debugInfo?.show && (
            <div className="alert alert-info mb-4">
              <pre className="mb-0">
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </div>
          )}

          {artists.length === 0 ? (
            <div className="alert alert-info">
              <h4 className="alert-heading">No Artists Found</h4>
              <p>You haven't followed any artists yet. Try following some artists on Spotify!</p>
            </div>
          ) : (
            <div className="row row-cols-1 row-cols-md-3 g-4">
              {artists.map(artist => (
                <div key={artist.id} className="col">
                  <div className="card h-100">
                    <div className="card-img-top bg-dark" style={{ height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {artist.images && artist.images.length > 0 ? (
                        <img 
                          src={artist.images[0].url}
                          className="card-img-top"
                          alt={artist.name}
                          style={{ height: '200px', objectFit: 'cover' }}
                        />
                      ) : (
                        <div className="text-center text-white">
                          <i className="bi bi-person" style={{ fontSize: '3rem' }}></i>
                        </div>
                      )}
                    </div>
                    <div className="card-body">
                      <h5 className="card-title text-truncate">{artist.name}</h5>
                      <p className="card-text">
                        {artist.followers?.total?.toLocaleString() || 0} followers
                        <br />
                        {artist.genres?.slice(0, 2).join(', ') || 'No genres listed'}
                      </p>
                      {artist.external_urls?.spotify && (
                        <a 
                          href={artist.external_urls.spotify} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn btn-outline-success btn-sm"
                        >
                          Open in Spotify
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    </div>
  );
};

export default Artists;